import * as FavoritesUpdatedEvent from '@ori-events/favorites-updated';
import { MenuTypes } from '@ori-events/toggle-menu-flyout';
import * as UserAction from '@ori-events/user-action';
import { Heart, HeartFilled } from '@ori-ui/icons';
import { ButtonBase, styled } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import { HeartsAnimation } from '@ori/favorites-heart';
import { useAreFavoritesEnabled } from '@ori/presentation-hooks';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { CLICK_FAVORITE_ICON } from '../constants';
import { useActiveMenu } from '../context/navigation';
import { useHover } from '../hooks';
import { FavoritesLink } from '../models';
import { dispatchToggleMenu } from '../utils';

const FavoritesHeartRoot = styled(ButtonBase)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    color: theme.palette.grey[600],
    display: 'inline-flex',
    fontSize: theme.typography.pxToRem(30),
    height: 'fit-content',
    marginLeft: theme.spacing(4),

    '&:hover, &:focus, &:active': {
      color: theme.palette.grey[600],
      cursor: 'pointer',
    },

    '& div:first-of-type': {
      alignItems: 'center',
      display: 'flex',
    },
  },

  [theme.breakpoints.up('lg')]: {
    marginLeft: theme.spacing(6),
  },

  [theme.breakpoints.up('xl')]: {
    marginLeft: theme.spacing(8),
  },
}));

const emptyFunction = () => {};

export const FavoritesHeartIcon: FC = () => {
  const [ref, Icon] = useHover(HeartFilled, Heart);
  const [wasFavoriteAdded, setWasFavoriteAdded] = useState(false);
  const { activeMenu } = useActiveMenu();
  const { customerId } = useToken();
  const shouldFavoritesRender = useAreFavoritesEnabled({ customerId });
  const { getTestId } = useTestId();

  const handleClick = useCallback(() => {
    UserAction.dispatch(CLICK_FAVORITE_ICON);

    if (window.location.pathname === FavoritesLink) {
      return;
    }

    dispatchToggleMenu(MenuTypes.FavoritesSideMenu, activeMenu);
  }, [activeMenu]);

  const onFavoritesUpdated = ({ detail: { favorites, lastUpdated } }: FavoritesUpdatedEvent.Event) => {
    if (lastUpdated && favorites.includes(lastUpdated)) {
      setWasFavoriteAdded(() => true);

      setTimeout(() => {
        setWasFavoriteAdded(() => false);
      }, 0);
    }
  };

  useEffect(() => {
    window.addEventListener(FavoritesUpdatedEvent.Name, onFavoritesUpdated as EventListener);

    return () => {
      window.removeEventListener(FavoritesUpdatedEvent.Name, onFavoritesUpdated as EventListener);
    };
  }, []);

  if (!shouldFavoritesRender) {
    return null;
  }

  return (
    <FavoritesHeartRoot
      ref={ref}
      disableRipple
      data-testid={getTestId('favorite-heart-root')}
      onClick={handleClick}
    >
      <HeartsAnimation
        shouldAnimationRun
        state={wasFavoriteAdded}
        onClick={emptyFunction}
      >
        <Icon fontSize="inherit" />
      </HeartsAnimation>
    </FavoritesHeartRoot>
  );
};
